import React, { useEffect, useState } from 'react';
import './VehicleResult.scss';
import RentalInputsContext from '@app/context/rental/RentalInputsContext';

const icons = {
    toll: 'https://cdn1.acedms.com/photos/images/obv-web/rental/tool.svg',
    parking: 'https://cdn1.acedms.com/photos/images/obv-web/rental/parking.svg',
    driverNightCharges: 'https://cdn1.acedms.com/photos/images/obv-web/rental/driver.svg',
    stateTax: 'https://cdn1.acedms.com/photos/images/obv-web/rental/tax-new.svg',
    driver: 'https://cdn1.acedms.com/photos/images/obv-web/rental/driver-new.svg'
};

const tripDistanceMapForHourly = {
    4: 40,
    8: 80,
    12: 120
};

const VehicleRentalCard = () => {
    const { rentalInputs, setRentalInputs } = React.useContext(RentalInputsContext);

    const [productData, setProductData] = useState(null);
    const [basePriceData, setBasePriceData] = useState(null);
    const [priceData, setPriceData] = useState(null);
    const [totalDistance, setTotalDistance] = useState(null);
    const [selectedTripType, setSelectedTripType] = useState(rentalInputs?.trip_type || 'one_way');

    const [getRentalPriceInputs, setGetRentalPriceInputs] = useState({
        make: '',
        model: '',
        trip_type: rentalInputs?.trip_type,
        start_city: rentalInputs?.start_city,
        end_city: rentalInputs?.end_city,
        start_date: rentalInputs?.start_date,
        end_date: rentalInputs?.end_date,
        trip_hours: rentalInputs?.trip_hours
    });

    const getProduct = () => {
        if (!rentalInputs?.product_title) return;

        fetch(`/rentals/get-rental-products?product_title=${encodeURIComponent(rentalInputs.product_title)}`)
            .then(res => res.json().catch(() => ({ data: [] })))
            .then(data => {
                if (data?.code === 'success' && Array.isArray(data.data) && data.data.length > 0) {
                    const product = data.data[0];

                    setProductData(product);

                    setGetRentalPriceInputs(prev => ({
                        ...prev,
                        make: product.make,
                        model: product.model
                    }));

                    setRentalInputs(prev => ({
                        ...prev,
                        photo : product.photo
                    }));
                }
            })
            .catch(err => {
                console.error('Error fetching product:', err);
            });
    };

    const ucwords = (str = '') =>{
        return str
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase());
    }

    const getPrice = () => {
        if (!getRentalPriceInputs.make || !getRentalPriceInputs.model) return;
        setTotalDistance(null);

        if (rentalInputs?.trip_type === 'hourly') {
            const hourlyParams = {
                ...getRentalPriceInputs,
                end_city: getRentalPriceInputs.start_city,  // force end_city to match start_city
                end_date: getRentalPriceInputs.start_date
            };

            const params = new URLSearchParams(hourlyParams).toString();
            fetch(`/rentals/get-rental-price?${params}`)
                .then(res => res.json())
                .then(data => {
                    if (data?.code === 'success' && data?.data?.rental_cost) {
                        setPriceData({ hourly: (data.data.rental_cost || 0) });
                    }
                    if (data?.code === 'success' && data?.data?.rental_cost) {
                        const cost = data.data.rental_cost;
                        setBasePriceData({ hourly: cost });
                        setPriceData(applyAddOnCharges({ hourly: cost }, rentalInputs?.add_ons_charge));
                        if (data.data?.total_distance) {
                            setTotalDistance(data.data.total_distance);
                        }
                    }
                    else {
                        setPriceData(null);
                        console.warn('Invalid hourly price data:', data);
                    }
                })
                .catch(err => console.error('Error fetching price:', err));
        } else {
            const baseParams = {
                ...getRentalPriceInputs,
                trip_type: 'one_way',
                end_date : rentalInputs?.start_date,
            };
            const roundTripParams = {
                ...getRentalPriceInputs,
                trip_type: 'round_trip'
            };

            Promise.all([
                fetch(`/rentals/get-rental-price?${new URLSearchParams(baseParams)}`).then(res => res.json()),
                fetch(`/rentals/get-rental-price?${new URLSearchParams(roundTripParams)}`).then(res => res.json())
            ])
                .then(([oneWayData, roundTripData]) => {
                    const newBasePrice = {};

                    if (oneWayData?.code === 'success' && oneWayData?.data?.rental_cost) {
                        newBasePrice.one_way = oneWayData.data.rental_cost;
                    }

                    if (roundTripData?.code === 'success' && roundTripData?.data?.rental_cost) {
                        newBasePrice.round_trip = roundTripData.data.rental_cost;
                    }

                    setBasePriceData(newBasePrice);
                    setPriceData(applyAddOnCharges(newBasePrice, rentalInputs?.add_ons_charge));

                    if (roundTripData?.data?.total_distance && rentalInputs?.trip_type==='round_trip') {
                        setTotalDistance(roundTripData.data.total_distance);
                    } 
                    if (oneWayData?.data?.total_distance && rentalInputs?.trip_type==='one_way') {
                        setTotalDistance(oneWayData.data.total_distance);
                    }
                })
                .catch(err => console.error('Error fetching prices:', err));
        }
    };


    const applyAddOnCharges = (basePriceData, addOnCharge) => {
        const addon = Number(addOnCharge) || 0;

        const updated = {};

        if (basePriceData?.one_way != null) {
            updated.one_way = Number(basePriceData.one_way) + addon;
        }

        if (basePriceData?.round_trip != null) {
            updated.round_trip = Number(basePriceData.round_trip) + addon;
        }

        if (basePriceData?.hourly != null) {
            updated.hourly = Number(basePriceData.hourly) + addon;
        }
        return updated;
    };

    const handleDateChange= (startDate) =>{
            const newEndDate = new Date(startDate);
            newEndDate.setDate(startDate.getDate() + 2);

            const formattedEndDate = newEndDate.toISOString().split('T')[0];

            setRentalInputs(prev => ({
                ...prev,
                end_date: formattedEndDate
            }));
    }

    useEffect(() => {
        if (!basePriceData) return;
        const updated = applyAddOnCharges(basePriceData, rentalInputs?.add_ons_charge);
        setPriceData(updated);
    }, [rentalInputs?.add_ons_charge]);



    useEffect(() => {
        getProduct();
    }, [rentalInputs?.product_title]);

    useEffect(() => {
        if (productData?.make && productData?.model) {

            const startDate = new Date(rentalInputs?.start_date);
            let endDate = new Date(rentalInputs?.end_date);

            if (!rentalInputs?.end_date || startDate > endDate) {
                handleDateChange(startDate);
                return;
            }

            setGetRentalPriceInputs(prev => ({
                ...prev,
                make: productData.make,
                model: productData.model,
                trip_type: rentalInputs?.trip_type,
                start_city: rentalInputs?.start_city,
                end_city: rentalInputs?.end_city,
                start_date: rentalInputs?.start_date,
                end_date: rentalInputs?.end_date,
                trip_hours: rentalInputs?.trip_hours
            }));
        }
      }, [productData?.make,productData?.model, rentalInputs, rentalInputs?.trip_hours,rentalInputs?.start_city,rentalInputs?.end_city,rentalInputs?.start_date,rentalInputs?.end_date]);


    useEffect(() => {
        if (getRentalPriceInputs.make && getRentalPriceInputs.model) {
            setPriceData(null);
            getPrice();
        }
    }, [getRentalPriceInputs.make, getRentalPriceInputs.model, getRentalPriceInputs.trip_type, getRentalPriceInputs.trip_hours,getRentalPriceInputs?.trip_hours,getRentalPriceInputs?.start_city,getRentalPriceInputs?.end_city,getRentalPriceInputs?.start_date,getRentalPriceInputs?.end_date]);



    return (
        <div className="vehicle-card py-4 px-4 bg-white rounded-lg shadow-sm">
            <div className="row">
                <div className="col-lg-5">
                    <div className="image-box">
                        <img
                            src={rentalInputs?.photo || ''}
                            alt={productData?.product_name_mm || 'Vehicle'}
                            className="vehicle-image mb-3 mb-lg-0 mr-lg-4"
                        />
                    </div>
                </div>
                <div className="col-lg-7">
                    <div className="flex-fill">
                        <p className="text-muted mb-1">{rentalInputs?.trip_type === 'hourly' ? 'Local Automobile Rental' : 'Outstation Automobile Rental'}</p>
                        <h4 className="grey">{ucwords(rentalInputs?.product_title || '')}</h4>
                        {priceData && (
                            rentalInputs?.trip_type !== 'hourly' ? (
                                <div className="mt-3">
                                    {selectedTripType === 'one_way' && typeof priceData.one_way === 'number' && (
                                        <div className="mb-2 text-center">
                                            <span className="text-orange font-size-24">
                                                ₹{Math.round(priceData.one_way * 0.97).toLocaleString()} - ₹{Math.round(priceData.one_way * 1.03).toLocaleString()}
                                            </span>
                                        </div>
                                    )}
                                    {selectedTripType === 'round_trip' && typeof priceData.round_trip === 'number' && (
                                        <div className="mb-2 text-center">
                                            <span className="text-orange font-size-24">
                                                ₹{Math.round(priceData.round_trip * 0.97).toLocaleString()} - ₹{Math.round(priceData.round_trip * 1.03).toLocaleString()}
                                            </span>
                                        </div>
                                    )}
                                    <div className="text-center">
                                        <div className="trip-type-toggle" style={{ display: 'inline-flex' }}>
                                            {typeof priceData.one_way === 'number' && (
                                                <button
                                                    className={`trip-pill${selectedTripType === 'one_way' ? ' active' : ''}`}
                                                    onClick={() => setSelectedTripType('one_way')}
                                                    type="button"
                                                >
                                                    One Way
                                                </button>
                                            )}
                                            {typeof priceData.round_trip === 'number' && (
                                                <button
                                                    className={`trip-pill${selectedTripType === 'round_trip' ? ' active' : ''}`}
                                                    onClick={() => setSelectedTripType('round_trip')}
                                                    type="button"
                                                >
                                                    Round Trip
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center mb-2">
                                        <img
                                            src="https://cdn1.acedms.com//photos/images/obv-web/rental/location-icons.svg"
                                            alt="location"
                                        />
                                        <span className="ml-2">{rentalInputs?.start_city}</span>
                                    </div>
                                    {typeof priceData.hourly === 'number' && (
                                        <div className="text-orange price-text text-center mb-2">
                                            <span>₹{Math.round(priceData.hourly * 0.97).toLocaleString()}</span> - <span>₹{Math.round(priceData.hourly * 1.03).toLocaleString()}</span>
                                        </div>
                                    )}
                                    <div className="trip-type-toggle local-toggle" style={{ display: 'flex', flexWrap: 'nowrap', borderRadius: '8px' }}>
                                        {[
                                            { value: '4', label: '4 hrs / 40 kms' },
                                            { value: '8', label: '8 hrs / 80 kms' },
                                            { value: '12', label: '12 hrs / 120 kms' }
                                        ].map(({ value, label }) => (
                                            <button
                                                key={value}
                                                type="button"
                                                className={`trip-pill${String(rentalInputs?.trip_hours || '8') === value ? ' active' : ''}`}
                                                onClick={() => {
                                                    setGetRentalPriceInputs(prev => ({ ...prev, trip_hours: value }));
                                                    setRentalInputs(prev => ({ ...prev, trip_hours: value }));
                                                }}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-3">
                <div className="d-flex align-items-center">
                    <img src="https://cdn1.acedms.com/photos/images/obv-web/rental/tick.svg" width="17" alt="icon" />
                    <h5 className="text-black ml-2 mt-1 grey">What is Included</h5>
                </div>
                <div className="row mt-1 gap-row">
                    <div className="col-lg-4 col-6">
                        <div className="d-flex align-items-center">
                            <img src="https://cdn1.acedms.com//photos/images/obv-web/rental/fule.svg" alt="icon" />
                            <p className="small mb-0 ml-2">Vehicle and fuel charges</p>
                        </div>
                    </div>
                    <div className="col-lg-4 col-6">
                        <div className="d-flex align-items-center">
                            <img src={icons.driver} alt="icon" />
                            <p className="small mb-0 ml-2">Driver Charges</p>
                        </div>
                    </div>
                    {totalDistance && (
                      <div className="col-lg-4 col-6">
                        <div className="d-flex align-items-center">
                            <img src="https://cdn1.acedms.com//photos/images/obv-web/rental/km-driven.svg" alt="icon" />
                                <p className="small mb-0 ml-2">
                                    {rentalInputs?.trip_type === 'hourly'
                                        ? `${tripDistanceMapForHourly[rentalInputs?.trip_hours] || rentalInputs?.trip_hours * 10} Kms Included`
                                        : `${totalDistance} Kms Included`}
                                </p>
                        </div>
                      </div>
                    )}
                </div>
            </div>
            <div className="mt-4">
                <div className="d-flex align-items-center">
                    <img src="https://cdn1.acedms.com/photos/images/obv-web/rental/cross.svg" width="15" alt="icon" />
                    <h5 className="text-black ml-2 mt-1 grey">What is not Included</h5>
                </div>
                <div className="row mt-2 gap-row">
                    <div className="col-lg-2 col-6">
                        <div className="d-flex align-items-center">
                            <img src={icons.toll} alt="Toll" />
                            <p className="small mb-0 ml-2">Toll Tax</p>
                        </div>
                    </div>
                    <div className="col-lg-2 col-6">
                        <div className="d-flex align-items-center">
                            <img src={icons.parking} alt="Parking" />
                            <p className="small mb-0 ml-2">Parking Charges</p>
                        </div>
                    </div>
                    <div className="col-lg-2 col-6">
                        <div className="d-flex align-items-center">
                            <img src={icons.stateTax} alt="Parking" />
                            <p className="small mb-0 ml-2">State Tax</p>
                        </div>
                    </div>
                    <div className="col-lg-2 col-6">
                        <div className="d-flex align-items-center">
                            <img src={icons.parking} alt="Parking" />
                            <p className="small mb-0 ml-2">Driver Tip</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VehicleRentalCard;